import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useLang } from '@/i18n/LangContext';

/**
 * Smooth-scrolls to `document.getElementById(hash)` whenever the URL hash
 * changes (or the route changes with a hash already present).
 *
 * Lazy-loaded route components mount AFTER the initial pathname-change render,
 * so the target element may not exist when this hook fires. The retry loop
 * (one rAF, then up to 7 polled retries at 75 ms intervals, ~600 ms worst case)
 * survives the Suspense boundary; if the element still does not exist after
 * the budget, the hook is a no-op rather than scrolling to a partial DOM.
 *
 * Call once at the App root — paired with App.tsx's page-change scroll-reset
 * effect, which skips its `scrollTo(0)` when a hash is present so this hook
 * owns the destination scroll position. Both key on the locale-stripped
 * `basePath`, so switching language (`/yacht#x` → `/it/yacht#x`) keeps the
 * viewport where it is instead of re-running the scroll.
 */
export function useHashScroll(): void {
  const { hash } = useLocation();
  const { basePath } = useLang();

  useEffect(() => {
    if (!hash) return;
    const id = hash.slice(1);
    if (!id) return;

    let attempts = 0;
    const maxAttempts = 8;
    const intervalMs = 75;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let raf: number | undefined;

    const tryScroll = () => {
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return;
      }
      attempts += 1;
      if (attempts < maxAttempts) {
        timer = setTimeout(tryScroll, intervalMs);
      }
    };

    raf = requestAnimationFrame(tryScroll);

    return () => {
      if (raf !== undefined) cancelAnimationFrame(raf);
      if (timer) clearTimeout(timer);
    };
  }, [hash, basePath]);
}
